import { datePl, type MeDto, RELEASE_AREAS, RELEASE_NOTES, type ReleaseNote, releaseItemsFor, type ReleaseSeenDto, unseenReleaseNotes } from '@bqcrm/shared';
import { Badge, Button, Group, Modal, Notification, Stack, Text, Title } from '@mantine/core';
import { IconRefresh, IconSparkles } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { can } from '../auth';
import { APP_VERSION } from './AppFooter';

const dayOf = (id: string) => datePl(id.slice(0, 10));

/** Wpisy z punktami widocznymi dla tej osoby (obszary według uprawnień). */
function visible(me: MeDto, notes: ReleaseNote[]) {
  return notes.map((n) => ({ ...n, items: releaseItemsFor(n, (p) => can(me, p)) })).filter((n) => n.items.length);
}

function NotesList({ notes }: { notes: ReleaseNote[] }) {
  return (
    <Stack gap="lg">
      {notes.map((n) => (
        <Stack key={n.id} gap={6}>
          <Title order={5}>{dayOf(n.id)}</Title>
          {n.items.map((i, k) => (
            <Group key={k} gap="xs" wrap="nowrap" align="flex-start">
              <Badge size="sm" variant="light" style={{ flexShrink: 0 }} w={130}>
                {RELEASE_AREAS[i.area].label}
              </Badge>
              <Text size="sm">{i.text}</Text>
            </Group>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * „Co nowego”: po aktualizacji (także przy zapamiętanej sesji — przy każdym otwarciu CRM) panel ze zmianami,
 * których osoba jeszcze nie widziała; `history` — cała lista z menu użytkownika.
 */
export function WhatsNew({ me, history, onHistoryClose }: { me: MeDto; history: boolean; onHistoryClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['me-release'], queryFn: () => api<ReleaseSeenDto>('/me/release'), staleTime: Infinity });
  const latest = RELEASE_NOTES[0];
  const unseen = q.data && latest ? visible(me, unseenReleaseNotes(q.data.seen)) : [];
  const [closed, setClosed] = useState(false);
  const close = () => {
    setClosed(true);
    if (!latest) return;
    qc.setQueryData<ReleaseSeenDto>(['me-release'], { seen: latest.id });
    void api('/me/release', { method: 'PUT', body: { id: latest.id } }).catch(() => undefined);
  };
  // wersja bez punktów dla tej osoby — od razu obejrzana (panel nie pokaże się przy następnym wydaniu z powodu starego wpisu)
  useEffect(() => {
    if (q.data && latest && q.data.seen !== latest.id && !unseen.length) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, latest?.id, unseen.length]);

  return (
    <>
      <Modal
        opened={!closed && unseen.length > 0 && !history}
        onClose={close}
        size="lg"
        title={
          <Group gap="xs">
            <IconSparkles size={20} />
            <Text fw={700}>Co nowego w tej wersji</Text>
          </Group>
        }
      >
        <Stack>
          <NotesList notes={unseen} />
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Wcześniejsze zmiany: menu użytkownika → „Co nowego”.
            </Text>
            <Button onClick={close}>Rozumiem</Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={history} onClose={onHistoryClose} size="lg" title="Co nowego — historia zmian">
        <NotesList notes={visible(me, RELEASE_NOTES)} />
      </Modal>
    </>
  );
}

/**
 * Karta otwarta w trakcie aktualizacji ma stary kod — co 5 minut i po powrocie do karty sprawdzamy wersję
 * na serwerze (`/wersja.txt` z budowania obrazu); nowsza → prośba o odświeżenie (potem panel „Co nowego”).
 */
export function NewVersionNotice() {
  const q = useQuery({
    queryKey: ['app-version'],
    queryFn: async () => {
      const r = await fetch('/wersja.txt', { cache: 'no-store' });
      const t = r.ok ? (await r.text()).trim() : '';
      return /^\d{4}\.\d{2}\.\d{2}-[0-9a-f]+$/.test(t) ? t : null;
    },
    enabled: APP_VERSION !== 'lokalna',
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  if (!q.data || q.data === APP_VERSION) return null;
  return (
    <Notification
      icon={<IconRefresh size={18} />}
      color="blue"
      title="Jest nowa wersja CRM"
      withCloseButton={false}
      style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 300, maxWidth: 360 }}
    >
      <Stack gap="xs">
        <Text size="sm">Odśwież stronę, aby z niej korzystać. Niezapisane zmiany w otwartym formularzu najpierw zapisz.</Text>
        <Group justify="flex-end">
          <Button size="xs" leftSection={<IconRefresh size={14} />} onClick={() => window.location.reload()}>
            Odśwież
          </Button>
        </Group>
      </Stack>
    </Notification>
  );
}
